/**
 * Remove caracteres não numéricos de uma string
 * @param {string} str - String a ser limpa
 * @returns {string} - String apenas com números
 */
const limparNumeros = (str) => {
  return str.replace(/\D/g, "");
};

/**
 * Valida se um CPF é válido
 * @param {string} cpf - CPF a ser validado (com ou sem formatação)
 * @returns {boolean} - true se o CPF for válido, false caso contrário
 */
export const validarCPF = (cpf) => {
  // Remove caracteres não numéricos
  const cpfLimpo = limparNumeros(cpf);

  // Verifica se tem 11 dígitos
  if (cpfLimpo.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(cpfLimpo)) {
    return false;
  }

  // Calcula o primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
  }
  let resto = soma % 11;
  let primeiroDigito = resto < 2 ? 0 : 11 - resto;

  // Verifica o primeiro dígito
  if (parseInt(cpfLimpo.charAt(9)) !== primeiroDigito) {
    return false;
  }

  // Calcula o segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
  }
  resto = soma % 11;
  let segundoDigito = resto < 2 ? 0 : 11 - resto;

  // Verifica o segundo dígito
  if (parseInt(cpfLimpo.charAt(10)) !== segundoDigito) {
    return false;
  }

  return true;
};

/**
 * Valida se um CNPJ é válido
 * @param {string} cnpj - CNPJ a ser validado (com ou sem formatação)
 * @returns {boolean} - true se o CNPJ for válido, false caso contrário
 */
export const validarCNPJ = (cnpj) => {
  // Remove caracteres não numéricos
  const cnpjLimpo = limparNumeros(cnpj);

  // Verifica se tem 14 dígitos
  if (cnpjLimpo.length !== 14) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CNPJ inválido)
  if (/^(\d)\1{13}$/.test(cnpjLimpo)) {
    return false;
  }

  // Calcula o primeiro dígito verificador
  let soma = 0;
  let peso = 2;
  for (let i = 11; i >= 0; i--) {
    soma += parseInt(cnpjLimpo.charAt(i)) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  let resto = soma % 11;
  let primeiroDigito = resto < 2 ? 0 : 11 - resto;

  // Verifica o primeiro dígito
  if (parseInt(cnpjLimpo.charAt(12)) !== primeiroDigito) {
    return false;
  }

  // Calcula o segundo dígito verificador
  soma = 0;
  peso = 2;
  for (let i = 12; i >= 0; i--) {
    soma += parseInt(cnpjLimpo.charAt(i)) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  resto = soma % 11;
  let segundoDigito = resto < 2 ? 0 : 11 - resto;

  // Verifica o segundo dígito
  if (parseInt(cnpjLimpo.charAt(13)) !== segundoDigito) {
    return false;
  }

  return true;
};

/**
 * Função utilitária para validar CPF ou CNPJ automaticamente
 * @param {string} documento - CPF ou CNPJ a ser validado
 * @returns {object} - { valido: boolean, tipo: 'CPF' | 'CNPJ' | 'INVALIDO' }
 */
export const validarDocumento = (documento) => {
  const documentoLimpo = limparNumeros(documento);

  if (documentoLimpo.length === 11) {
    return {
      valido: validarCPF(documento),
      tipo: "CPF",
    };
  } else if (documentoLimpo.length === 14) {
    return {
      valido: validarCNPJ(documento),
      tipo: "CNPJ",
    };
  } else {
    return {
      valido: false,
      tipo: "INVALIDO",
    };
  }
};

/**
 * Função simplificada para validar CPF ou CNPJ automaticamente
 * Retorna apenas true/false baseado na validação
 * @param {string} documento - CPF ou CNPJ a ser validado
 * @returns {boolean} - true se o documento for válido, false caso contrário
 */
export const validarCpfCnpj = (documento) => {
  const documentoLimpo = limparNumeros(documento);

  if (documentoLimpo.length === 11) {
    return validarCPF(documento);
  } else if (documentoLimpo.length === 14) {
    return validarCNPJ(documento);
  } else {
    return false;
  }
};

// Exportações padrão para compatibilidade
export default {
  validarCPF,
  validarCNPJ,
  validarDocumento,
  validarCpfCnpj,
};
